"use client";

import { useState } from "react";
import { Tooltip } from "@tillix/ui-atoms/tooltip";
import { Info } from "lucide-react";
import { useAppointmentT } from "../i18n/use-t";
import { getOrderRestrictionKey } from "../lib/status";
import type { OrderActionAvailability } from "../types";

interface OrderRestrictionTooltipProps {
  actions: OrderActionAvailability;
}

/**
 * Icon-only trigger explaining why an order can't be rescheduled online —
 * the same copy the order page shows inline. Renders nothing for orders
 * that can still be rescheduled. Opens immediately on hover or focus, and
 * on click/tap so it also works on touch devices.
 */
export default function OrderRestrictionTooltip({
  actions,
}: OrderRestrictionTooltipProps) {
  const t = useAppointmentT();
  const [open, setOpen] = useState(false);
  const messageKey = getOrderRestrictionKey(actions);
  if (!messageKey) return null;
  const message = t(messageKey);
  return (
    <Tooltip
      onOpenChange={(nextOpen, details) => {
        // Base UI dismisses the tooltip when its trigger is pressed; here a
        // press should open it instead, so let the click handler decide.
        if (!nextOpen && details.reason === "trigger-press") return;
        setOpen(nextOpen);
      }}
      open={open}
    >
      <Tooltip.Trigger
        delay={0}
        onClick={() => setOpen(true)}
        render={
          <button
            aria-label={message}
            className="text-muted-foreground hover:text-foreground"
            type="button"
          />
        }
      >
        <Info aria-hidden="true" className="size-4" />
      </Tooltip.Trigger>
      <Tooltip.Popup className="max-w-xs">{message}</Tooltip.Popup>
    </Tooltip>
  );
}
